import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';

import { ObjectWithFinancialData } from '../../models/Financial/ObjectWithFinancialData';
import Project from '../../models/Project';
import { ProjectInfoModalComponent } from '../../projects/project-info-modal/project-info-modal.component';
import { ProjectState } from '../../projects/project.state';
import { DateNavigationOptions, DateNavigationType, SelectedDateClass } from '../../shared/date-navigation/date-navigation.component';
import { GetProjectsFinancialData } from '../financial.actions';
import { FinancialState } from '../financial.state';
import { FinancialService } from '../financial.service';

@Component({
    selector: 'app-project-financial-graphs',
    templateUrl: './project-financial-graphs.component.html',
    styleUrls: ['./project-financial-graphs.component.scss'],
})
export class ProjectFinancialGraphsComponent implements OnInit, OnDestroy {
    @Select(ProjectState.projects) allProjects$: Observable<Project[]>;

    projectsWithFinancialData: ObjectWithFinancialData[];
    datasource: MatTableDataSource<ObjectWithFinancialData>;
    displayedColumns: string[] = ['project', 'margin', 'marginAmount', 'totalIncome', 'totalCosts'];

    selectedDate: Date = new Date();
    dateNavigationOptions: DateNavigationOptions;
    fetchedDataForCurrentYear = false;
    loadingData = false;
    showArchived = false;
    searchText = '';
    aiAnalyzing = false;
    aiAnalysisHtml: SafeHtml = null;
    sendingEmail = false;
    viewMode: 'year' | 'allTime' | 'running' = 'year';
    loadingAllTime = false;
    private allProjects: Project[] = [];

    private sSort: MatSort;
    private subscriptions = new Subscription();

    constructor(private store: Store, private modalService: NgbModal, private financialService: FinancialService, private sanitizer: DomSanitizer, private toastr: ToastrService) {}

    @ViewChild('sort') set sort(ms: MatSort) {
        this.sSort = ms;
        this.setDataSourceAttributes();
    }

    setDataSourceAttributes(): void {
        this.datasource.sort = this.sSort;
    }

    ngOnInit(): void {
        this.dateNavigationOptions = {
            dateNavigationType: DateNavigationType.YEAR,
            selectedDate: this.selectedDate,
            selectedDateClass: SelectedDateClass.SMALL,
        };

        this.subscriptions.add(
            this.allProjects$.subscribe((projects) => {
                this.allProjects = projects || [];
            })
        );

        this.createMatTable();
        this.fetchData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    openProjectDetail(projectFinancialData: ObjectWithFinancialData): void {
        const modalRef = this.modalService.open(ProjectInfoModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });
        modalRef.componentInstance.projectUuid = projectFinancialData.objectId;
        modalRef.componentInstance.openFinancialTab = true;
    }

    updateSelectedDate(): void {
        this.fetchedDataForCurrentYear = false;
        this.fetchData();
    }

    setViewMode(mode: 'year' | 'allTime' | 'running'): void {
        if (this.viewMode === mode) return;
        this.viewMode = mode;
        this.aiAnalysisHtml = null;
        if (mode === 'year') {
            this.fetchedDataForCurrentYear = false;
            this.fetchData();
        } else {
            this.fetchAllTimeData(mode === 'running');
        }
    }

    private fetchAllTimeData(currentOnly: boolean): void {
        this.loadingAllTime = true;
        this.projectsWithFinancialData = [];
        this.datasource.data = [];
        this.financialService.getProjectsFinancialDataAllTime(currentOnly).subscribe({
            next: (data) => {
                const normalized = (data || []).map(p => {
                    const totalIncome = this.safeNumber(p.totalIncome);
                    const totalCosts = this.safeNumber(p.totalCosts);
                    return { ...p, totalIncome, totalCosts } as any;
                });
                this.projectsWithFinancialData = normalized;
                this.datasource.data = normalized;
                this.datasource.sort = this.sSort;
                this.datasource.sortingDataAccessor = this.buildSortingDataAccessor();
                this.datasource.filterPredicate = this.buildFilterPredicate();
                this.applyCurrentFilter();
                this.loadingAllTime = false;
            },
            error: () => {
                this.toastr.error('Kon all-time data niet laden.');
                this.loadingAllTime = false;
            }
        });
    }

    filterData() {
        this.applyCurrentFilter();
    }

    applyFilter(event: Event): void {
        this.searchText = (event.target as HTMLInputElement).value.trim().toLowerCase();
        this.applyCurrentFilter();
    }

    // Summary helpers
    getActiveProjectCount(): number {
        return this.datasource?.filteredData?.length || 0;
    }

    getAvgMargin(): number {
        if (!this.datasource?.filteredData) return 0;
        const withData = this.datasource.filteredData.filter(d => d.totalIncome > 0 || d.totalCosts > 0);
        if (withData.length === 0) return 0;
        const sum = withData.reduce((acc, d) => acc + this.getMarginPercent(d), 0);
        return sum / withData.length;
    }

    getTotalIncome(): number {
        if (!this.datasource?.filteredData) return 0;
        return this.datasource.filteredData.reduce((acc, d) => acc + (d.totalIncome || 0), 0);
    }

    getTotalCosts(): number {
        if (!this.datasource?.filteredData) return 0;
        return this.datasource.filteredData.reduce((acc, d) => acc + (d.totalCosts || 0), 0);
    }

    getTotalMarginAmount(): number {
        return this.getTotalIncome() - this.getTotalCosts();
    }

    // Margin styling helpers
    getMarginPercent(row: ObjectWithFinancialData): number {
        const income = row.totalIncome || 0;
        const costs = row.totalCosts || 0;
        const margin = income - costs;
        if (margin === 0) return 0;
        if (income > 0) return (margin / income) * 100;
        if (costs > 0) return (margin / costs) * 100;
        return margin < 0 ? -100 : 0;
    }

    getMarginClass(row: ObjectWithFinancialData): string {
        const pct = this.getMarginPercent(row);
        if (pct >= 30) return 'margin-value--high';
        if (pct >= 15) return 'margin-value--medium';
        return 'margin-value--low';
    }

    getProjectCompanyName(objectId: string): string {
        const proj = this.allProjects.find(p => p.uuid === objectId);
        return proj?.companyName || '';
    }

    // Tooltip helpers
    private eur(value: number): string {
        return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
    }

    getCostsTooltip(row: ObjectWithFinancialData): string {
        const hours = row.totalHoursWorked || 0;
        const costs = row.totalCosts || 0;
        const lines: string[] = [];
        lines.push(`Gepresteerde uren: ${hours.toFixed(1)}h`);
        if (hours > 0) {
            lines.push(`Gem. kost/uur: ${this.eur(costs / hours)}`);
        }
        if (row.hoursBreakdown?.length) {
            lines.push('');
            lines.push('Per medewerker:');
            row.hoursBreakdown.forEach(h => lines.push('• ' + h));
        }
        return lines.join('\n');
    }

    getIncomeTooltip(row: ObjectWithFinancialData): string {
        const hours = row.totalHoursWorked || 0;
        const income = row.totalIncome || 0;
        const lines: string[] = [];
        lines.push(`Totaal: ${this.eur(income)}`);
        if (hours > 0) {
            lines.push(`Gem. opbrengst/uur: ${this.eur(income / hours)}`);
        }
        if (row.incomeBreakdown?.length) {
            lines.push('');
            lines.push('Per factuur/klant:');
            row.incomeBreakdown.forEach(i => lines.push('• ' + i));
        }
        return lines.join('\n');
    }

    getMarginTooltip(row: ObjectWithFinancialData): string {
        const income = row.totalIncome || 0;
        const costs = row.totalCosts || 0;
        const margin = income - costs;
        const hours = row.totalHoursWorked || 0;
        const lines: string[] = [];
        lines.push(`Inkomen: ${this.eur(income)}`);
        lines.push(`Kosten: ${this.eur(costs)}`);
        lines.push(`Marge: ${this.eur(margin)} (${this.getMarginPercent(row).toFixed(0)}%)`);
        if (hours > 0) {
            lines.push(`Marge/uur: ${this.eur(margin / hours)}`);
        }
        return lines.join('\n');
    }

    // AI Analysis
    runAiAnalysis(): void {
        this.aiAnalyzing = true;
        const year = this.dateNavigationOptions.selectedDate.getFullYear();

        const data = this.datasource.filteredData.map(row => {
            const proj = this.allProjects.find(p => p.uuid === row.objectId);
            const income = Math.round(Number(row.totalIncome) || 0);
            const costs = Math.round(Number(row.totalCosts) || 0);
            const marginEur = income - costs;
            // Use the same margin definition as the table so the AI sees identical %.
            const marginPct = Math.round(this.getMarginPercent(row));
            return {
                name: proj?.title || 'Unknown',
                company: proj?.companyName || '',
                division: this.getDivisionLabel(proj?.projectType),
                totalIncome: income,
                totalCosts: costs,
                marginEur,
                marginPct,
            };
        });

        // Make the input visible to the user/dev so we can audit what the AI receives.
        // eslint-disable-next-line no-console
        console.log('[AI Analyse] Input naar AI:', { year, projects: data });

        this.financialService.analyzeProjectFinancials(year, data).subscribe({
            next: (res) => {
                this.aiAnalysisHtml = this.sanitizer.bypassSecurityTrustHtml(this.formatAnalysis(res.analysis));
                this.aiAnalyzing = false;
            },
            error: () => {
                this.toastr.error('Er ging iets mis bij de AI analyse.');
                this.aiAnalyzing = false;
            }
        });
    }

    // Send Email
    sendEmail(): void {
        const email = prompt('Enter email address:');
        if (!email) return;
        this.sendingEmail = true;
        const year = this.dateNavigationOptions.selectedDate.getFullYear();
        this.financialService.sendProjectFinancialEmail(year, email).subscribe({
            next: () => { this.toastr.success('Email sent!'); this.sendingEmail = false; },
            error: () => { this.toastr.error('Failed to send email.'); this.sendingEmail = false; }
        });
    }

    isValidNumber(value: any): boolean {
        const n = Number(value);
        return value != null && !isNaN(n) && isFinite(n);
    }

    private readonly projectTypeLabels: Record<number, string> = {
        0: 'Custom',
        1: 'Website',
        2: 'Consultancy',
        3: 'Other',
        4: 'Hosting',
        5: 'Intern',
    };

    private readonly projectTypeNameLabels: Record<string, string> = {
        CUSTOM: 'Custom',
        WEBSITE: 'Website',
        CONSULTANCY: 'Consultancy',
        OTHER: 'Other',
        HOSTING: 'Hosting',
        INTERN: 'Intern',
    };

    private getDivisionLabel(projectType: number | string | undefined): string {
        if (projectType == null || projectType === '') return 'Overig';
        if (typeof projectType === 'number') {
            return this.projectTypeLabels[projectType] || 'Overig';
        }
        const key = String(projectType).toUpperCase();
        return this.projectTypeNameLabels[key] || 'Overig';
    }

    private formatAnalysis(text: string): string {
        if (!text) {
            return '';
        }

        const normalized = text.replace(/\r\n/g, '\n').trim();
        if (!normalized) {
            return '';
        }

        const sections = this.extractAnalysisSections(normalized);
        if (!sections.length) {
            return `<section class="ai-section"><div class="ai-section__body">${this.formatSectionBody('', normalized)}</div></section>`;
        }

        return sections
            .map(
                (section) =>
                    `<section class="ai-section">` +
                    `<h5 class="ai-section__title">${this.escapeHtml(section.title)}</h5>` +
                    `<div class="ai-section__body">${this.formatSectionBody(section.title, section.content)}</div>` +
                    `</section>`,
            )
            .join('');
    }

    private extractAnalysisSections(text: string): Array<{ title: string; content: string }> {
        const sectionPattern = /^(SAMENVATTING|AANDACHTSPUNTEN|ACTIEPUNTEN|DETAILS|CONCLUSIE)\s*:?\s*$/gim;
        const matches: Array<RegExpExecArray & { index: number }> = [];
        let m: RegExpExecArray | null;
        // eslint-disable-next-line no-cond-assign
        while ((m = sectionPattern.exec(text)) !== null) {
            matches.push(m as RegExpExecArray & { index: number });
        }

        if (!matches.length) {
            return [];
        }

        const sections: Array<{ title: string; content: string }> = [];
        const firstMatch = matches[0];
        if (firstMatch.index > 0) {
            const intro = text.slice(0, firstMatch.index).trim();
            if (intro) {
                sections.push({ title: 'OVERZICHT', content: intro });
            }
        }

        matches.forEach((match, index) => {
            const start = match.index + match[0].length;
            const end = index + 1 < matches.length ? matches[index + 1].index : text.length;
            const content = text.slice(start, end).trim();
            sections.push({ title: (match[1] || '').toUpperCase(), content });
        });

        return sections.filter((section) => !!section.content);
    }

    private formatSectionBody(sectionTitle: string, rawText: string): string {
        const clean = rawText.trim();
        if (!clean) {
            return '<p>Geen inhoud beschikbaar.</p>';
        }

        const upperTitle = sectionTitle.toUpperCase();

        // ACTIEPUNTEN/AANDACHTSPUNTEN are grouped per AFDELING (division)
        if (upperTitle === 'ACTIEPUNTEN' || upperTitle === 'AANDACHTSPUNTEN') {
            const grouped = this.renderDivisionGroups(clean);
            if (grouped) {
                return grouped;
            }
            // Fallback to flat list if no AFDELING markers were emitted
            const fallbackLines = clean
                .split('\n')
                .map((line) => line.trim())
                .filter((line) => !!line);
            return this.renderList(fallbackLines);
        }

        const paragraphs = clean
            .split(/\n{2,}/)
            .map((chunk) => chunk.trim())
            .filter((chunk) => !!chunk);

        return paragraphs
            .map((paragraph) => {
                const paragraphLines = paragraph
                    .split('\n')
                    .map((line) => line.trim())
                    .filter((line) => !!line);

                const isListBlock =
                    paragraphLines.length > 0 &&
                    paragraphLines.every((line) => /^([-*•]|\d+[.)])\s+/.test(line));

                if (isListBlock) {
                    return this.renderList(paragraphLines);
                }

                const merged = paragraphLines.join(' ');
                return `<p>${this.formatInline(merged)}</p>`;
            })
            .join('');
    }

    private renderDivisionGroups(rawText: string): string {
        const lines = rawText.split('\n').map((l) => l.trim()).filter((l) => !!l);
        const groups: Array<{ name: string; items: string[] }> = [];
        let current: { name: string; items: string[] } | null = null;

        for (const line of lines) {
            const headerMatch = line.match(/^AFDELING\s*:\s*(.+?)\s*:?\s*$/i);
            if (headerMatch) {
                current = { name: headerMatch[1].trim() || 'Overig', items: [] };
                groups.push(current);
                continue;
            }
            if (!current) {
                // No header yet → start a default group so we still render bullets
                current = { name: 'Algemeen', items: [] };
                groups.push(current);
            }
            current.items.push(line);
        }

        const validGroups = groups.filter((g) => g.items.length > 0);
        if (!validGroups.length) {
            return '';
        }

        return validGroups
            .map(
                (group) =>
                    `<div class="ai-division">` +
                    `<div class="ai-division__title">${this.escapeHtml(group.name.toUpperCase())}</div>` +
                    `<div class="ai-division__body">${this.renderList(group.items)}</div>` +
                    `</div>`,
            )
            .join('');
    }

    private renderList(lines: string[]): string {
        if (!lines.length) {
            return '';
        }

        const items = lines
            .map((line) => line.replace(/^([-*•]|\d+[.)])\s+/, '').trim())
            .filter((line) => !!line)
            .map((line) => `<li>${this.formatInline(line)}</li>`)
            .join('');

        return items ? `<ul class="ai-list">${items}</ul>` : '';
    }

    private formatInline(text: string): string {
        return this.escapeHtml(text).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    }

    private escapeHtml(text: string): string {
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    private applyCurrentFilter(): void {
        this.datasource.filter = `${this.showArchived}|${this.searchText}|${Date.now()}`;
    }

    private fetchData(): void {
        const year: number = this.dateNavigationOptions.selectedDate.getFullYear();

        this.subscriptions.add(
            this.store.select(FinancialState.projectsFinancialDataForYear(year)).subscribe((projectsFinancialData) => {
                if (!this.fetchedDataForCurrentYear && (!projectsFinancialData || projectsFinancialData.length < 1)) {
                    this.fetchedDataForCurrentYear = true;
                    this.loadingData = true;

                    this.subscriptions.add(
                        this.store.dispatch(new GetProjectsFinancialData(year)).subscribe({
                            error: () => {
                                this.loadingData = false;
                            },
                            complete: () => {
                                this.loadingData = false;
                            },
                        }),
                    );
                }

                if (!projectsFinancialData || projectsFinancialData.length === 0) {
                    this.projectsWithFinancialData = [];
                    this.datasource.data = [];
                    return;
                }

                const normalized = (projectsFinancialData || []).map(p => {
                    const totalIncome = this.safeNumber(p.totalIncome);
                    const totalCosts = this.safeNumber(p.totalCosts);
                    const marginPct = totalIncome > 0
                        ? (totalIncome - totalCosts) / totalIncome
                        : (totalCosts > 0 ? (totalIncome - totalCosts) / totalCosts : 0);
                    return {
                        ...p,
                        totalIncome,
                        totalCosts,
                        displayMarginPct: marginPct,
                    } as any;
                });

                this.projectsWithFinancialData = normalized;
                this.datasource.data = normalized;
                this.datasource.sort = this.sSort;

                this.datasource.sortingDataAccessor = this.buildSortingDataAccessor();
                this.datasource.filterPredicate = this.buildFilterPredicate();

                this.applyCurrentFilter();
                this.loadingData = false;
            }),
        );
    }

    private buildSortingDataAccessor(): (item: any, property: string) => any {
        return (item: any, property: string) => {
            switch (property) {
                case 'project':
                    const project = this.allProjects.find(p => p.uuid === item.objectId);
                    return project?.title?.toLowerCase() || '';
                case 'margin':
                    return this.getMarginPercent(item);
                case 'marginAmount':
                    return item.totalIncome - item.totalCosts;
                case 'totalCosts':
                    return item.totalCosts || 0;
                case 'totalIncome':
                    return item.totalIncome || 0;
                default:
                    return (item as any)[property];
            }
        };
    }

    private buildFilterPredicate(): (data: ObjectWithFinancialData, filter: string) => boolean {
        return (data: ObjectWithFinancialData, _filter: string) => {
            if (!this.showArchived && data.archived) return false;
            // In year mode, skip projects with zero activity
            if (this.viewMode === 'year' && (data.totalIncome || 0) === 0 && (data.totalCosts || 0) === 0) return false;
            if (!this.searchText) return true;
            const project = this.allProjects.find(p => p.uuid === data.objectId);
            const projectTitle = project?.title ? project.title.toLowerCase() : '';
            const companyName = project?.companyName ? project.companyName.toLowerCase() : '';
            return projectTitle.includes(this.searchText) || companyName.includes(this.searchText);
        };
    }

    private safeNumber(value: any): number {
        const n = Number(value);
        if (value === null || value === undefined || isNaN(n) || !isFinite(n)) {
            return 0;
        }
        return n;
    }

    private createMatTable(): void {
        this.datasource = new MatTableDataSource<ObjectWithFinancialData>();
    }
}
